import Confetti from "@/assets/illustrations/confetti.json";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import {
  getDataKeluargaTambahan,
  getDataSiswaLengkap,
  getListBerkas,
  getListDataAset,
  getListDataKeluarga,
} from "@/services/getData";
import { Item, useDataValidasi } from "@/store/dataValidasiStore";
import {
  DataAset as DataAsetType,
  DataBerkas as DataBerkasType,
  DataKeluarga as DataKeluargaType,
  DataSiswa,
  DataSurvey as DataSurveyType,
  SysBerkas,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import Lottie from "lottie-react";
import {
  AlertTriangle,
  Briefcase,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  Download,
  FileCheck,
  FileSignature,
  FileText,
  Home,
  Info,
  Loader2,
  Lock,
  Unlock,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaFilePdf } from "react-icons/fa6";
import DataAset from "./DataAset";
import DataBerkas from "./DataBerkas";
import DataBiodata from "./DataBiodata";
import DataKeluarga from "./DataKeluarga";
import DataSurvey from "./DataSurvey";

const patchData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/daftar-kembali/validasi-permanen",
    newData,
    {
      withCredentials: true,
    },
  );
  return response.data;
};

const patchDataUnvalid = async (newData: FormData) => {
  const id_data_pendaftaran = newData.get("id_data_pendaftaran");

  const response = await api.patch(
    "/siswa/daftar-kembali/unvalidasi-permanen",
    { id_data_pendaftaran },
    {
      withCredentials: true,
    },
  );
  return response.data;
};

export default function Validasi({
  idDataPendaftaran,
  isValidPermanen,
  isWaktuSelesaiIsiUktSelesai,
  isValidPermanenTanpaCekTanggal,
}: {
  idDataPendaftaran: string;
  isValidPermanen: boolean;
  isWaktuSelesaiIsiUktSelesai: boolean | null;
  isValidPermanenTanpaCekTanggal: boolean;
}) {
  const validasiRef = useRef<HTMLDivElement | null>(null);

  // --- NEW: REFS UNTUK SCROLL TARGET ---
  const biodataRef = useRef<HTMLElement>(null);
  const keluargaRef = useRef<HTMLElement>(null);
  const asetRef = useRef<HTMLElement>(null);
  const berkasRef = useRef<HTMLElement>(null);
  // ------------------------------------

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [showLottie, setShowLottie] = useState(false);
  const [form, setForm] = useState<DataSurveyType>({
    id_data_survey: "",
    id_data_pendaftaran: idDataPendaftaran || "",
    ket_biaya_hidup_lebih_penghasilan: "",
    mengetahui_undiksha: "",
    kenapa_undiksha: "",
    url_fb: "",
    twitter: "",
    instagram: "",
    tiktok: "",
  });

  const { user } = useUser();

  // --- QUERIES ---
  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataSiswaLengkap", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getDataSiswaLengkap(sessionPeriode?.id_sys_periode),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
      refetchOnWindowFocus: false,
    });
  const { data: listDataKeluarga, isPending: isPendingListDataKeluarga } =
    useQuery<DataKeluargaType[]>({
      queryKey: ["listDataKeluarga", sessionPeriode?.id_sys_periode],
      queryFn: () => getListDataKeluarga(sessionPeriode?.id_sys_periode || ""),
      refetchOnWindowFocus: false,
    });
  const {
    data: dataKeluargaTambahan,
    isPending: isPendingDataKeluargaTambahan,
  } = useQuery({
    queryKey: ["dataKeluargaTambahan", idDataPendaftaran],
    queryFn: () => getDataKeluargaTambahan(idDataPendaftaran),
    refetchOnWindowFocus: false,
  });
  const { data: listDataAset, isPending: isPendingListDataAset } = useQuery<
    DataAsetType[]
  >({
    queryKey: ["listDataAset"],
    queryFn: () => getListDataAset(sessionPeriode?.id_sys_periode || ""),
    refetchOnWindowFocus: false,
  });
  const { data: listDataBerkas, isPending: isPendingListDataBerkas } =
    useQuery<{
      listSysBerkas: SysBerkas[];
      listDataBerkasSiswa: DataBerkasType[];
    }>({
      queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
      queryFn: () => getListBerkas(sessionPeriode?.id_sys_periode || ""),
      refetchOnWindowFocus: false,
    });

  const filteredBerkasSuratPernyataan = listDataBerkas?.listSysBerkas?.filter(
    (sysBerkas: SysBerkas) => {
      return sysBerkas.jenis_berkas?.slug_jenis_berkas == "Surat_Pernyataan";
    },
  );

  const [isTermsChecked, setIsTermsCheked] = useState(false);
  const [showValidasiPermanen, setShowValidasiPermanen] = useState(false);
  const [showUnvalidasiPermanen, setShowUnvalidasiPermanen] = useState(false);

  // State Accordion Control
  const [activeSection, setActiveSection] = useState<string | null>("Biodata");

  const [fileSuratPernyataan, setFileSuratPernyataan] = useState<File | null>(
    null,
  );

  const {
    dataBiodata,
    dataListKeluarga,
    dataListAset,
    dataListBerkas,
    dataKeluargaTambahan: dataKeluargaTambahanValidasiState,
    setDataBiodata,
    setDataListKeluarga,
    setDataKeluargaTambahan,
    setDataListAset,
    setDataListBerkas,
  } = useDataValidasi();

  const isAllBiodataValidated =
    dataBiodata.length > 0
      ? dataBiodata.every((item) => item.isValidated)
      : false;
  const isAllKeluargaValidated =
    dataListKeluarga.length > 0 && dataKeluargaTambahanValidasiState
      ? dataListKeluarga.every((item) => item.isValidated) &&
        dataKeluargaTambahanValidasiState.isValidated
      : false;
  const isAllAsetValidated =
    dataListAset.length > 0
      ? dataListAset.every((item) => item.isValidated)
      : false;
  const isAllBerkasValidated =
    dataListBerkas?.listSysBerkas && dataListBerkas?.listSysBerkas.length > 0
      ? dataListBerkas?.listSysBerkas.every((item) => item.isValidated)
      : false;

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: patchData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataPendaftaran"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({
        queryKey: ["dataStatusValidasiPermanen"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dataStatusIsiUKTpenerimaKIPK"],
      });
      toast.success(data?.message || "Data berhasil divalidasi permanen.");
      setShowValidasiPermanen(false);
      setShowLottie(true);
      setTimeout(() => {
        setShowLottie(false);
      }, 5000);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan yang tidak terduga.";
      toast.error(errorMessage);
    },
  });

  const {
    mutate: mutateUnvalid,
    isPending: isPendingUnvalid,
    error: errorUnvalid,
  } = useMutation({
    mutationFn: patchDataUnvalid,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataPendaftaran"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({
        queryKey: ["dataStatusValidasiPermanen"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dataStatusIsiUKTpenerimaKIPK"],
      });
      toast.success(data?.message || "Data berhasil divalidasi permanen.");
      setShowUnvalidasiPermanen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan yang tidak terduga.";
      toast.error(errorMessage);
    },
  });

  const handleSubmitValidasiPermanen = (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    const formData = new FormData();
    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append(
      "ket_biaya_hidup_lebih_penghasilan",
      form.ket_biaya_hidup_lebih_penghasilan,
    );
    formData.append("mengetahui_undiksha", form.mengetahui_undiksha);
    formData.append("kenapa_undiksha", form.kenapa_undiksha);
    if (form.url_fb) formData.append("url_fb", form.url_fb);
    if (form.twitter) formData.append("twitter", form.twitter);
    if (form.instagram) formData.append("instagram", form.instagram);
    if (form.tiktok) formData.append("tiktok", form.tiktok);
    formData.append("id_sys_periode", String(sessionPeriode?.id_sys_periode));

    if (fileSuratPernyataan) {
      formData.append("file_berkas_surat_pernyataan", fileSuratPernyataan);
    }

    mutate(formData);
  };

  const handleSubmitUnvalidasiPermanen = (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    const formData = new FormData();
    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);

    mutateUnvalid(formData);
  };

  // --- USE EFFECTS (Logic Validasi) ---
  useEffect(() => {
    if (!dataSiswa) return;
    const dataValidasiString = localStorage.getItem("dataValidasi");
    const savedData = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : { biodata: [] };

    const updatedDataBiodata: Item[] = [
      {
        label: "Nama Lengkap",
        value: dataSiswa?.nama_lengkap || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Nama Lengkap")
            ?.isValidated || false,
      },
      {
        label: "Tanggal Lahir",
        value: dataSiswa?.tanggal_lahir || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Tanggal Lahir",
          )?.isValidated || false,
      },
      {
        label: "Tempat Lahir",
        value: dataSiswa?.tempat_lahir || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Tempat Lahir")
            ?.isValidated || false,
      },
      {
        label: "Agama",
        value: dataSiswa?.agama?.nama_agama || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Agama")
            ?.isValidated || false,
      },
      {
        label: "Nomor Telepon",
        value: dataSiswa?.handphone || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Nomor Telepon",
          )?.isValidated || false,
      },
      {
        label: "NPWP",
        value: dataSiswa?.npwp || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "NPWP")
            ?.isValidated || false,
      },
      {
        label: "Jenis Kelamin",
        value: dataSiswa?.jenis_kelamin || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Jenis Kelamin",
          )?.isValidated || false,
      },
      {
        label: "Berat Badan",
        value: dataSiswa?.berat_badan || 0,
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Berat Badan")
            ?.isValidated || false,
      },
      {
        label: "Tinggi Badan",
        value: dataSiswa?.tinggi_badan || 0,
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Tinggi Badan")
            ?.isValidated || false,
      },
      {
        label: "Ukuran Baju",
        value: dataSiswa?.ukuran_baju || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Ukuran Baju")
            ?.isValidated || false,
      },
      {
        label: "Alat Transportasi",
        value: dataSiswa?.alat_transportasi?.nama_alat_transportasi || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Alat Transportasi",
          )?.isValidated || false,
      },
      {
        label: "Kewarganegaraan",
        value: dataSiswa?.negara?.nama_negara || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Kewarganegaraan",
          )?.isValidated || false,
      },
      {
        label: "Alamat",
        value: dataSiswa?.jalan || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Alamat")
            ?.isValidated || false,
      },
      {
        label: "RT",
        value: dataSiswa?.rt || 0,
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "RT")
            ?.isValidated || false,
      },
      {
        label: "RW",
        value: dataSiswa?.rw || 0,
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "RW")
            ?.isValidated || false,
      },
      {
        label: "Kode Pos",
        value: dataSiswa?.kode_pos || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Kode Pos")
            ?.isValidated || false,
      },
      {
        label: "Desa/Kelurahan",
        value: dataSiswa?.kelurahan || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Desa/Kelurahan",
          )?.isValidated || false,
      },
      {
        label: "Provinsi",
        value: dataSiswa?.ref_provinsi?.nama_wilayah || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Provinsi")
            ?.isValidated || false,
      },
      {
        label: "Kabupaten",
        value: dataSiswa?.ref_kabupaten?.nama_wilayah || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Kabupaten")
            ?.isValidated || false,
      },
      {
        label: "Kecamatan",
        value: dataSiswa?.ref_kecamatan?.nama_wilayah || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Kecamatan")
            ?.isValidated || false,
      },
      {
        label: "Sekolah Asal",
        value: dataSiswa?.sekolah?.nama_sekolah || "",
        isValidated:
          savedData.biodata?.find((item: Item) => item.label === "Sekolah Asal")
            ?.isValidated || false,
      },
      {
        label: "Kebutuhan Khusus",
        value: dataSiswa?.kebutuhan_khusus?.join(", ") || "",
        isValidated:
          savedData.biodata?.find(
            (item: Item) => item.label === "Kebutuhan Khusus",
          )?.isValidated || false,
      },
    ];
    setDataBiodata(updatedDataBiodata);
  }, [dataSiswa, setDataBiodata]);

  useEffect(() => {
    if (!listDataKeluarga) return;
    const dataValidasiString = localStorage.getItem("dataValidasi");
    const dataValidasi = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : { keluarga: [] };
    const updatedDataListKeluarga = listDataKeluarga.map((item) => {
      const matchedData = dataValidasi.keluarga.find(
        (storedItem: { id_data_keluarga: string }) =>
          storedItem.id_data_keluarga === item.id_data_keluarga,
      );
      return { ...item, isValidated: matchedData?.isValidated || false };
    });
    setDataListKeluarga(updatedDataListKeluarga);
  }, [listDataKeluarga, setDataListKeluarga]);

  useEffect(() => {
    if (!dataKeluargaTambahan) return;
    const dataValidasiString = localStorage.getItem("dataValidasi");
    const dataValidasi = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : { keluarga_tambahan: null };
    const updatedDataKeluargaTambahan = dataValidasi?.keluarga_tambahan
      ? dataValidasi?.keluarga_tambahan
      : dataKeluargaTambahan;
    setDataKeluargaTambahan({
      ...dataKeluargaTambahan,
      isValidated: updatedDataKeluargaTambahan?.isValidated || false,
    });
  }, [dataKeluargaTambahan, setDataKeluargaTambahan]);

  useEffect(() => {
    if (!listDataAset) return;
    const dataValidasiString = localStorage.getItem("dataValidasi");
    const dataValidasi = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : { aset: [] };
    const updatedDataListAset = listDataAset.map((item) => {
      const matchedData = dataValidasi.aset?.find(
        (storedItem: { id_data_aset: string }) =>
          storedItem.id_data_aset === item.id_data_aset,
      );
      return { ...item, isValidated: matchedData?.isValidated || false };
    });
    setDataListAset(updatedDataListAset);
  }, [listDataAset, setDataListAset]);

  useEffect(() => {
    if (!listDataBerkas) return;
    const dataValidasiString = localStorage.getItem("dataValidasi");
    const dataValidasi = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : { berkas: [] };
    const filteredListBerkas = listDataBerkas?.listSysBerkas?.filter(
      (sysBerkas) => {
        return sysBerkas.jenis_berkas?.slug_jenis_berkas != "Surat_Pernyataan";
      },
    );
    const updatedListSysBerkas =
      filteredListBerkas?.length > 0
        ? filteredListBerkas
            .map((item) => {
              const matchedData = dataValidasi.berkas?.listSysBerkas?.find(
                (storedItem: { id_sys_berkas: number }) =>
                  storedItem.id_sys_berkas === item.id_sys_berkas,
              );
              return {
                ...item,
                isValidated: matchedData?.isValidated || false,
              };
            })
            .filter((item) =>
              listDataBerkas.listDataBerkasSiswa?.some(
                (berkas) => berkas.id_sys_berkas == String(item.id_sys_berkas),
              ),
            )
        : [];
    const updatedDataListBerkas = {
      listSysBerkas: updatedListSysBerkas,
      listDataBerkasSiswa: listDataBerkas.listDataBerkasSiswa,
    };
    setDataListBerkas(updatedDataListBerkas);
  }, [listDataBerkas, setDataListBerkas]);

  // --- LOGIC: AUTO ADVANCE ACCORDION ---
  const prevBiodataValidated = useRef(isAllBiodataValidated);
  const prevKeluargaValidated = useRef(isAllKeluargaValidated);
  const prevAsetValidated = useRef(isAllAsetValidated);

  useEffect(() => {
    // 1. Jika Biodata baru saja selesai divalidasi, pindah ke Keluarga
    if (isAllBiodataValidated && !prevBiodataValidated.current) {
      if (!isAllKeluargaValidated) {
        setTimeout(() => setActiveSection("Keluarga"), 300);
      }
    }
    prevBiodataValidated.current = isAllBiodataValidated;

    // 2. Jika Keluarga baru saja selesai divalidasi, pindah ke Aset
    if (isAllKeluargaValidated && !prevKeluargaValidated.current) {
      if (!isAllAsetValidated) {
        setTimeout(() => setActiveSection("Aset"), 300);
      }
    }
    prevKeluargaValidated.current = isAllKeluargaValidated;

    // 3. Jika Aset baru saja selesai divalidasi, pindah ke Berkas
    if (isAllAsetValidated && !prevAsetValidated.current) {
      if (!isAllBerkasValidated) {
        setTimeout(() => setActiveSection("Berkas"), 300);
      }
    }
    prevAsetValidated.current = isAllAsetValidated;
  }, [
    isAllBiodataValidated,
    isAllKeluargaValidated,
    isAllAsetValidated,
    isAllBerkasValidated,
  ]);

  // --- NEW: LOGIC SCROLL TO ACTIVE SECTION ---
  useEffect(() => {
    // Tambahkan "| null" di dalam HTMLElement
    const refs: Record<string, React.RefObject<HTMLElement | null>> = {
      Biodata: biodataRef,
      Keluarga: keluargaRef,
      Aset: asetRef,
      Berkas: berkasRef,
    };

    if (activeSection && refs[activeSection]?.current) {
      setTimeout(() => {
        const element = refs[activeSection].current;
        if (element) {
          const offset = 100;
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - offset,
            behavior: "smooth",
          });
        }
      }, 150);
    }
  }, [activeSection]);
  // ------------------------------------------

  useEffect(() => {
    if (validasiRef && validasiRef.current) {
      const offset = -100;
      const elementPosition =
        validasiRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition + offset, behavior: "smooth" });
    }
  }, []);

  const berkasSuratPernyataan = listDataBerkas?.listDataBerkasSiswa?.find(
    (dataBerkas: DataBerkasType) => {
      return (
        dataBerkas?.sys_berkas?.jenis_berkas?.slug_jenis_berkas ==
        "Surat_Pernyataan"
      );
    },
  );

  const toggleSection = (section: string) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  // Helper Component for Step Header
  const StepHeader = ({
    title,
    isValidated,
    isOpen,
    icon: Icon,
    onClick,
  }: any) => (
    <div
      className={`p-4 flex items-center justify-between cursor-pointer transition-colors ${
        isOpen
          ? "bg-slate-50 dark:bg-slate-800/50"
          : "hover:bg-slate-50 dark:hover:bg-slate-800/30"
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <div
          className={`h-10 w-10 rounded-full flex justify-center items-center shrink-0 border-2 transition-colors ${
            isValidated
              ? "bg-green-100 border-green-200 text-green-600 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400"
              : "bg-white border-slate-200 text-slate-400 dark:bg-slate-900 dark:border-slate-700"
          }`}
        >
          {isValidated ? (
            <CheckCircle2 className="w-6 h-6" />
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">
            {title}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {isValidated ? "Data sudah divalidasi" : "Cek dan validasi data"}
          </p>
        </div>
      </div>
      <div
        className={`transition-transform duration-300 ${
          isOpen ? "rotate-180" : ""
        }`}
      >
        <ChevronDown className="w-5 h-5 text-slate-400" />
      </div>
    </div>
  );

  return (
    <div
      ref={validasiRef}
      className="relative space-y-8 animate-in fade-in duration-500"
    >
      {/* --- STATUS HEADER --- */}
      <div>
        {isValidPermanenTanpaCekTanggal ? (
          <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <AlertTitle className="text-green-800 dark:text-green-300 font-bold">
              Validasi Permanen Selesai
            </AlertTitle>
            <AlertDescription className="text-green-700 dark:text-green-400 text-sm mt-2">
              <p className="mb-4">
                Data Anda telah tersimpan secara permanen dan tidak dapat diubah
                kembali. Terima kasih telah melengkapi data.
              </p>
              <Button
                variant="outline"
                className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-50"
                onClick={() => {
                  if (!berkasSuratPernyataan) return;
                  window.open(berkasSuratPernyataan?.path_berkas, "_blank");
                }}
              >
                <FaFilePdf className="mr-2 text-red-500" /> Lihat Surat
                Pernyataan
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
            <Info className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <AlertTitle className="text-amber-800 dark:text-amber-300 font-bold">
              Verifikasi Data Final
            </AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-400 text-sm mt-1">
              Mohon periksa kembali seluruh data Anda sebelum melakukan Validasi
              Permanen. Pastikan tidak ada kesalahan penulisan atau dokumen yang
              salah.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* --- ACCORDION SECTIONS DENGAN REFS --- */}
      <div className="flex flex-col gap-4">
        {/* 1. BIODATA */}
        <section
          ref={biodataRef}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm scroll-mt-24"
        >
          <StepHeader
            title="Biodata Diri"
            isValidated={isValidPermanen || isAllBiodataValidated}
            isOpen={activeSection === "Biodata"}
            icon={User}
            onClick={() => toggleSection("Biodata")}
          />
          {activeSection === "Biodata" && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-4">
              <DataBiodata
                isPending={isPendingDataSiswa}
                isValidPermanen={isValidPermanen}
              />
            </div>
          )}
        </section>

        {/* 2. KELUARGA */}
        <section
          ref={keluargaRef}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm scroll-mt-24"
        >
          <StepHeader
            title="Data Keluarga"
            isValidated={isValidPermanen || isAllKeluargaValidated}
            isOpen={activeSection === "Keluarga"}
            icon={Briefcase} // Atau Users
            onClick={() => toggleSection("Keluarga")}
          />
          {activeSection === "Keluarga" && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-4">
              <DataKeluarga
                isPending={isPendingListDataKeluarga}
                isPendingDataKeluargaTambahan={isPendingDataKeluargaTambahan}
                isValidPermanen={isValidPermanen}
              />
            </div>
          )}
        </section>

        {/* 3. ASET */}
        <section
          ref={asetRef}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm scroll-mt-24"
        >
          <StepHeader
            title="Data Aset"
            isValidated={isValidPermanen || isAllAsetValidated}
            isOpen={activeSection === "Aset"}
            icon={Home}
            onClick={() => toggleSection("Aset")}
          />
          {activeSection === "Aset" && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-4">
              <DataAset
                isPending={isPendingListDataAset}
                isValidPermanen={isValidPermanen}
              />
            </div>
          )}
        </section>

        {/* 4. BERKAS */}
        <section
          ref={berkasRef}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm scroll-mt-24"
        >
          <StepHeader
            title="Berkas Pendukung"
            isValidated={isValidPermanen || isAllBerkasValidated}
            isOpen={activeSection === "Berkas"}
            icon={FileCheck}
            onClick={() => toggleSection("Berkas")}
          />
          {activeSection === "Berkas" && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-4">
              <DataBerkas
                isPending={isPendingListDataBerkas}
                isValidPermanen={isValidPermanen}
              />
            </div>
          )}
        </section>
      </div>

      {showLottie && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="flex items-center justify-center">
            <Lottie animationData={Confetti} loop={true} autoplay={true} />
          </div>
        </div>
      )}

      {/* --- VALIDASI PERMANEN ACTION --- */}
      {!isValidPermanen ? (
        <div className="relative overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm mt-10">
          {/* Aksen Garis Atas */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600 dark:bg-blue-500"></div>

          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-xl">
                <FileSignature className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-xl text-slate-900 dark:text-white">
                  Tahap Akhir: Validasi Permanen
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
                  Pastikan semua data dan berkas sudah benar sebelum mengunci
                  pendaftaran.
                </p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              {/* KOTAK KIRI: UNDUH SURAT PERNYATAAN DENGAN PERINGATAN */}
              <div className="lg:col-span-5 flex flex-col justify-center p-5 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/50 rounded-xl relative overflow-hidden">
                {/* Aksen Background */}
                <div className="absolute -right-4 -top-4 bg-blue-100 dark:bg-blue-900/30 w-24 h-24 rounded-full blur-2xl opacity-50 pointer-events-none"></div>

                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  Instruksi Surat Pernyataan
                </h4>

                <div className="text-sm text-blue-800 dark:text-blue-200/90 mb-5 leading-relaxed space-y-2 z-10">
                  <p>
                    Peserta <b>wajib mengunggah</b> surat pernyataan yang telah
                    dihasilkan oleh sistem.
                  </p>
                  <p>
                    Surat pernyataan ini harus{" "}
                    <b>
                      dicetak, ditandatangani, kemudian discan dalam format PDF
                    </b>{" "}
                    sebelum diunggah kembali ke sistem.
                  </p>
                </div>
                {filteredBerkasSuratPernyataan?.map((row) => {
                  return (
                    <Button
                      key={row.id_sys_berkas}
                      className="w-full h-auto py-2.5 whitespace-normal text-center leading-snug"
                      onClick={() =>
                        window.open(
                          `${row?.url_template_berkas}?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                          "_blank",
                        )
                      }
                    >
                      <Download className="w-4 h-4 mr-2 shrink-0" />
                      <span>Download Format Surat Pernyataan</span>
                    </Button>
                  );
                })}
              </div>

              {/* KOTAK KANAN: PERSETUJUAN & TOMBOL LOCK (Tetap sama seperti sebelumnya) */}
              <div className="lg:col-span-7 flex flex-col gap-4">
                <div
                  className={`p-4 border rounded-xl transition-all duration-300 ${
                    isTermsChecked
                      ? "bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                      : "bg-amber-50/30 border-amber-200 dark:bg-amber-950/10 dark:border-amber-900/50"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms"
                      checked={isTermsChecked}
                      onCheckedChange={() => setIsTermsCheked((prev) => !prev)}
                      className={`mt-1 transition-colors ${isTermsChecked ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" : ""}`}
                    />
                    <Label
                      htmlFor="terms"
                      className={`text-sm leading-relaxed cursor-pointer select-none transition-colors ${
                        isTermsChecked
                          ? "text-slate-700 dark:text-slate-300"
                          : "text-slate-600 dark:text-slate-400"
                      }`}
                    >
                      Saya menyatakan bahwa seluruh data yang saya isi dan
                      berkas yang saya unggah adalah{" "}
                      <b>benar, sah, dan sesuai dengan ketentuan.</b> Apabila
                      dikemudian hari ditemukan pemalsuan data, saya bersedia
                      menerima sanksi pembatalan kelulusan.
                    </Label>
                  </div>
                </div>

                <Button
                  size="lg"
                  variant={"dark"}
                  className={`group w-full min-h-[3.5rem] h-auto py-3 text-sm sm:text-base font-bold transition-all duration-300 ${
                    isTermsChecked
                      ? ""
                      : "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200 shadow-none dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500"
                  }`}
                  onClick={() => {
                    if (!isAllBiodataValidated)
                      return toast.error(
                        "Mohon validasi semua data pada bagian Biodata.",
                      );
                    if (!isAllKeluargaValidated)
                      return toast.error(
                        "Mohon validasi semua data pada bagian Keluarga.",
                      );
                    if (!isAllAsetValidated)
                      return toast.error(
                        "Mohon validasi semua data pada bagian Aset.",
                      );
                    if (!isAllBerkasValidated)
                      return toast.error(
                        "Mohon validasi semua data pada bagian Berkas.",
                      );
                    if (!isTermsChecked)
                      return toast.error(
                        "Silakan setujui pernyataan terlebih dahulu.",
                      );

                    setShowValidasiPermanen(true);
                  }}
                  disabled={!isTermsChecked}
                >
                  <div className="flex items-center justify-center gap-2 sm:gap-2.5">
                    {isTermsChecked ? (
                      <Lock className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 transition-transform duration-300 group-hover:scale-110" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 opacity-70" />
                    )}
                    <span className="tracking-wide whitespace-normal text-center leading-snug">
                      Lakukan Validasi Permanen
                    </span>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : !isWaktuSelesaiIsiUktSelesai ? (
        /* --- TAMPILAN KETIKA SUDAH VALIDASI PERMANEN --- */
        <div className="relative overflow-hidden bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl shadow-sm mt-10">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-emerald-500"></div>

          <div className="p-8 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>

            <h3 className="font-bold text-2xl text-slate-900 dark:text-white mb-2">
              Data Sudah Divalidasi Permanen
            </h3>

            <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-8">
              Pendaftaran Anda telah dikunci. Apabila terdapat kesalahan dan
              Anda perlu mengubah data, silakan batalkan validasi permanen
              terlebih dahulu.
            </p>

            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 dark:border-red-900/50 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
              onClick={() => setShowUnvalidasiPermanen(true)}
            >
              <Unlock className="w-4 h-4 mr-2" />
              Batalkan Validasi Permanen
            </Button>
          </div>
        </div>
      ) : (
        ""
      )}

      {/* --- MODAL CONFIRMATION --- */}
      <Modal
        show={showValidasiPermanen}
        onClose={() => setShowValidasiPermanen(false)}
        className="lg:w-[50vw] md:w-[70vw] w-[95vw]"
        closeable={!isPending}
      >
        <form onSubmit={handleSubmitValidasiPermanen} className="lg:p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Konfirmasi Validasi Permanen
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">
              Langkah terakhir! Unggah surat pernyataan yang telah
              ditandatangani dan lengkapi survey singkat.
            </p>
          </div>

          <div className="space-y-6">
            {/* Surat Pernyataan Upload Section */}
            {filteredBerkasSuratPernyataan?.map((row: SysBerkas) => (
              <div
                key={row.id_sys_berkas}
                className="bg-slate-50 dark:bg-slate-950 p-4 rounded-lg border border-slate-200 dark:border-slate-800"
              >
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-semibold">
                    Upload Surat Pernyataan
                  </Label>
                  <Badge
                    variant="outline"
                    className="cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800"
                    onClick={() =>
                      window.open(
                        `${row.url_template_berkas}?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                        "_blank",
                      )
                    }
                  >
                    <FileText className="w-3 h-3 mr-1" /> Download Template
                  </Badge>
                </div>

                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="bg-white dark:bg-slate-900"
                  onChange={(e) => {
                    const file = e.target.files?.[0];

                    const maxKb = row?.max_upload ?? 1024;
                    const maxBytes = maxKb * 1024;

                    if (file) {
                      if (file.size > maxBytes) {
                        const sizeInMb = (maxKb / 1024).toFixed(0);
                        toast.error(
                          `Ukuran file terlalu besar! Maksimal adalah ${sizeInMb}MB.`,
                        );

                        e.target.value = "";
                        return;
                      }

                      setFileSuratPernyataan(file);
                      toast.success("File berhasil dipilih.");
                    }
                  }}
                />

                <div className="mt-2 flex flex-wrap gap-2">
                  {row.extension_allowed?.map((ext) => (
                    <Badge
                      key={ext}
                      variant="secondary"
                      className="text-[10px]"
                    >
                      .{ext}
                    </Badge>
                  ))}
                  <span className="text-xs text-slate-400 ml-auto">
                    Max: {row.max_upload}KB
                  </span>
                </div>
                <InputError
                  text={error?.response?.data?.errors?.file_berkas_surat_pernyataan?.join(
                    ",",
                  )}
                />
              </div>
            ))}

            {/* Survey Form Section */}
            <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
              <DataSurvey
                idDataPendaftaran={idDataPendaftaran}
                error={error}
                form={form}
                setForm={setForm}
              />
            </div>

            {/* Footer Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-300 dark:border-slate-700"
                onClick={() => setShowValidasiPermanen(false)}
                disabled={isPending}
              >
                Batal
              </Button>
              <Button type="submit" className="w-full " disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Memproses...
                  </>
                ) : (
                  "Ya, Validasi Sekarang"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* MODAL KONFIRMASI UNVALIDASI PERMANEN */}
      <Modal
        show={showUnvalidasiPermanen}
        onClose={() => setShowUnvalidasiPermanen(false)}
        className="lg:w-[35vw] md:w-[50vw] w-[95vw]"
        closeable={!isPendingUnvalid}
      >
        <form onSubmit={handleSubmitUnvalidasiPermanen} className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-4">
              <Unlock className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Batalkan Validasi Permanen?
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">
              Dengan membatalkan validasi, berkas Surat Pernyataan yang telah
              Anda unggah mungkin akan dihapus, dan Anda harus melakukan proses
              Validasi Permanen kembali setelah selesai melakukan perubahan
              data.
            </p>
          </div>

          {/* Menampilkan error jika ada error dari API */}
          {errorUnvalid && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm text-center">
              {errorUnvalid.response?.data?.message ||
                "Terjadi kesalahan saat membatalkan validasi."}
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-800 mt-6">
            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-300 dark:border-slate-700"
              onClick={() => setShowUnvalidasiPermanen(false)}
              disabled={isPendingUnvalid}
            >
              Kembali
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="w-full"
              disabled={isPendingUnvalid}
            >
              {isPendingUnvalid ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Memproses...
                </>
              ) : (
                "Ya, Batalkan Validasi"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
